import type { PendingInvitation } from "@better-bookkeeping/auth-client/invitations";
import { INVITATION_CONTINUATION_SEARCH_PARAM } from "@/lib/auth-flow-redirects";
import { invitationClient } from "@/lib/auth-client";
import { z } from "zod";

export const INVITE_TOKEN_SEARCH_PARAM = "invite_token";
export const INVITATION_PACKAGE_TOKEN_SEARCH_PARAM = "invitation_package_token";
const LEGACY_PRICING_INVITE_TOKEN_SEARCH_PARAM = "pricing_invite_token";
const PACKAGE_TOKEN_PREFIX = "package:";

export function invitationEntryUrlWithoutCapability(
  pathname: string,
  search: string,
  hash: string,
): string {
  const params = new URLSearchParams(search.startsWith("?") ? search.slice(1) : search);
  params.delete(INVITE_TOKEN_SEARCH_PARAM);
  params.delete(LEGACY_PRICING_INVITE_TOKEN_SEARCH_PARAM);
  params.delete(INVITATION_PACKAGE_TOKEN_SEARCH_PARAM);
  params.delete(INVITATION_CONTINUATION_SEARCH_PARAM);
  const query = params.toString();
  return `${pathname}${query ? `?${query}` : ""}${hash}`;
}

export function withInviteTokenSearch<TShape extends z.ZodRawShape>(shape: TShape) {
  return z
    .object({
      ...shape,
      [INVITE_TOKEN_SEARCH_PARAM]: z.string().optional(),
      [LEGACY_PRICING_INVITE_TOKEN_SEARCH_PARAM]: z.string().optional(),
      [INVITATION_PACKAGE_TOKEN_SEARCH_PARAM]: z.string().optional(),
      [INVITATION_CONTINUATION_SEARCH_PARAM]: z
        .string()
        .regex(/^[A-Za-z0-9_-]{32}$/)
        .optional(),
    })
    .transform((search) => {
      const packageToken = search.invitation_package_token?.trim();
      if (packageToken) {
        return { ...search, invite_token: `${PACKAGE_TOKEN_PREFIX}${packageToken}` };
      }
      const inviteToken = search.invite_token ?? search.pricing_invite_token;
      if (!inviteToken) return search;
      return { ...search, invite_token: inviteToken };
    });
}

const STORAGE_KEY = "abacus.pendingInviteToken";

const CONTINUATION_STORAGE_KEY = "abacus.pendingInviteContinuation";

export function parsePendingInvitation(token: string | undefined): PendingInvitation | null {
  const normalizedToken = token?.trim();
  if (!normalizedToken) return null;
  if (normalizedToken.startsWith(PACKAGE_TOKEN_PREFIX)) {
    const packageToken = normalizedToken.slice(PACKAGE_TOKEN_PREFIX.length);
    return packageToken ? { kind: "package", token: packageToken } : null;
  }
  return { kind: "legacy", token: normalizedToken };
}

export function storePendingInviteToken(token: string | undefined) {
  const invitation = parsePendingInvitation(token);
  if (!invitation || typeof window === "undefined") return;
  window.sessionStorage.removeItem(CONTINUATION_STORAGE_KEY);
  window.sessionStorage.setItem(
    STORAGE_KEY,
    invitation.kind === "package" ? `${PACKAGE_TOKEN_PREFIX}${invitation.token}` : invitation.token,
  );
}

export function getPendingInvitation() {
  if (typeof window === "undefined") return null;
  return parsePendingInvitation(window.sessionStorage.getItem(STORAGE_KEY) ?? undefined);
}

export function getPendingInviteToken() {
  return getPendingInvitation()?.token ?? null;
}
export function getPendingInvitationContinuationId() {
  if (typeof window === "undefined") return null;
  const continuationId = window.sessionStorage.getItem(CONTINUATION_STORAGE_KEY);
  return continuationId && /^[A-Za-z0-9_-]{32}$/.test(continuationId) ? continuationId : null;
}

export function clearPendingInviteToken() {
  if (typeof window === "undefined") return;
  window.sessionStorage.removeItem(STORAGE_KEY);
  window.sessionStorage.removeItem(CONTINUATION_STORAGE_KEY);
}

export function hasPendingInviteToken() {
  return getPendingInvitation() !== null;
}

export function isUnavailableInvitationError(error: unknown): boolean {
  return (
    typeof error === "object" &&
    error !== null &&
    (Reflect.get(error, "code") === "GONE" || Reflect.get(error, "status") === 410)
  );
}

export async function preparePendingInvitationAuth(token: string | undefined) {
  const suppliedInvitation = parsePendingInvitation(token);
  if (suppliedInvitation) storePendingInviteToken(token);
  const invitation = suppliedInvitation ?? getPendingInvitation();
  if (!invitation) return { ready: true as const, continuationId: null };
  const prepared = await invitationClient.invitationPackage.prepareAuth({
    invitation,
    continuationId: getPendingInvitationContinuationId() ?? undefined,
  });
  if (prepared.continuationId) {
    window.sessionStorage.setItem(CONTINUATION_STORAGE_KEY, prepared.continuationId);
  }
  return prepared;
}

export async function resumePendingInvitationAuth(continuationId: string) {
  const restored = await invitationClient.invitationPackage.resumeAuth({ continuationId });
  const token =
    restored.invitation.kind === "package"
      ? `${PACKAGE_TOKEN_PREFIX}${restored.invitation.token}`
      : restored.invitation.token;
  storePendingInviteToken(token);
  window.sessionStorage.setItem(CONTINUATION_STORAGE_KEY, restored.continuationId);
  return restored;
}

export async function confirmPendingInvitation(authUserId: string) {
  const invitation = getPendingInvitation();
  if (!invitation) return { kind: "none" as const };
  const result =
    invitation.kind === "package"
      ? await invitationClient.invitationPackage.confirm({
          token: invitation.token,
          confirmedAuthUserId: authUserId,
          continuationId: getPendingInvitationContinuationId() ?? undefined,
        })
      : await invitationClient.pricingInvite.confirm({
          token: invitation.token,
          confirmedAuthUserId: authUserId,
          continuationId: getPendingInvitationContinuationId() ?? undefined,
        });
  clearPendingInviteToken();
  return { kind: "confirmed" as const, invitationKind: invitation.kind, result };
}
